import { useCallback, useEffect, useRef, useState } from "react";
import { X, Check, AlertTriangle, ArrowRight } from "lucide-react";
import { getReadyForMatchCandidate, markReadyForMatch } from "../readyForMatch/readyForMatchApi.js";
import { DetailSkeleton } from "../components/Skeleton.jsx";
import { maskPassport } from "./matchingWorkspaceModel.js";

/**
 * The last look at a worker's profile before they enter matching.
 *
 * WHY A DIALOG. Marking a worker ready is a commit, not a toggle: the backend
 * treats "not already marked" as a precondition (see the readiness checklist
 * it returns), so there is no un-send. A step that cannot be taken back gets
 * its own moment, with the facts it depends on in front of the person taking
 * it - rather than a button on a table row that acts on one click and shows
 * its reasons nowhere.
 *
 * WHAT IS SHOWN IS WHAT THE SERVER SAYS. The checklist, the eligibility, and
 * whether the button may be pressed at all are read from the candidate detail
 * endpoint (readiness.checklist / readiness.readyEligibility /
 * nextActions.canMarkReadyForMatch). This component never re-derives
 * readiness from the fields it happens to have on screen: a second opinion
 * computed here would eventually disagree with the one the server enforces,
 * and the operator would be the last to find out.
 */

const TITLE_ID = "wx-mw-confirm-title";

/** Splits the server's checklist into the items that block matching and the ones that only enrich the profile. */
export function splitChecklist(checklist) {
  const items = Array.isArray(checklist) ? checklist : [];
  return {
    blocking: items.filter((item) => item.blocking),
    quality: items.filter((item) => !item.blocking),
  };
}

export default function ConfirmForMatchingDialog({ workerId, onClose, onConfirmed }) {
  const dialogRef = useRef(null);
  const [state, setState] = useState({ status: "loading", detail: null, error: null });
  const [submit, setSubmit] = useState({ status: "idle", error: null });
  const [showQuality, setShowQuality] = useState(false);

  const load = useCallback(() => {
    if (!workerId) {
      return undefined;
    }
    let cancelled = false;
    setState({ status: "loading", detail: null, error: null });
    getReadyForMatchCandidate(workerId)
      .then((detail) => {
        if (!cancelled) {
          setState({ status: "ready", detail, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", detail: null, error });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [workerId]);

  useEffect(() => load(), [load]);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  async function handleConfirm() {
    setSubmit({ status: "submitting", error: null });
    try {
      await markReadyForMatch(workerId);
      setSubmit({ status: "done", error: null });
      // The pool this dialog was opened from lists workers who are NOT yet
      // marked, so this person is about to leave it. The parent refreshes;
      // the dialog stays open to say so, rather than vanishing along with
      // the row and leaving the operator wondering whether it worked.
      onConfirmed?.(workerId);
    } catch (error) {
      setSubmit({ status: "error", error });
    }
  }

  const detail = state.detail;
  const readiness = detail?.readiness ?? null;
  const { blocking, quality } = splitChecklist(readiness?.checklist);
  const missingBlocking = blocking.filter((item) => !item.filled);
  const canSend = Boolean(detail?.nextActions?.canMarkReadyForMatch) && readiness?.readyEligibility === true;
  const submitting = submit.status === "submitting";
  const done = submit.status === "done";

  return (
    <div
      className="wx-mw-confirm-root"
      onMouseDown={(event) => {
        if (dialogRef.current && !dialogRef.current.contains(event.target)) {
          onClose();
        }
      }}
    >
      <div className="wx-mw-confirm-backdrop" aria-hidden="true" />
      <div ref={dialogRef} className="wx-mw-confirm" role="dialog" aria-modal="true" aria-labelledby={TITLE_ID}>
        <header className="wx-mw-confirm-head">
          <div>
            <p className="wx-mw-confirm-kicker">ยืนยันก่อนส่งเข้าจับคู่งาน</p>
            <h2 id={TITLE_ID} className="wx-mw-confirm-title">
              {detail?.fullName ?? "กำลังโหลด..."}
            </h2>
            {detail && <p className="wx-mw-confirm-code">{detail.workerCode ?? detail.workerId}</p>}
          </div>
          <button type="button" className="wx-mw-confirm-close" onClick={onClose} aria-label="ปิด">
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="wx-mw-confirm-body">
          {state.status === "loading" && <DetailSkeleton label="กำลังโหลดข้อมูลแรงงาน..." sections={2} />}

          {state.status === "error" && (
            <div className="wx-mw-confirm-state" role="alert">
              <p>โหลดข้อมูลแรงงานรายนี้ไม่สำเร็จ</p>
              <button type="button" className="wx-button wx-button--ghost" onClick={load}>
                ลองใหม่
              </button>
            </div>
          )}

          {state.status === "ready" && detail && (
            <>
              <section className="wx-mw-confirm-identity">
                <div>
                  <span>สัญชาติ</span>
                  <strong>{detail.nationality ?? "ไม่ระบุ"}</strong>
                </div>
                <div>
                  <span>ภาษา</span>
                  <strong>{detail.preferredLanguageCode ?? "ไม่ระบุ"}</strong>
                </div>
                <div>
                  <span>หนังสือเดินทาง</span>
                  <strong>{maskPassport(detail.passportNumber) ?? "ยังไม่มีในระบบ"}</strong>
                </div>
                <div>
                  <span>สายงาน</span>
                  <strong>{detail.agent?.displayName ?? detail.agent?.agentCode ?? "ไม่ระบุ"}</strong>
                </div>
              </section>

              <section className="wx-mw-confirm-section">
                <h3>
                  ข้อมูลที่จำเป็นก่อนจับคู่
                  <span className="wx-mw-confirm-count">
                    {blocking.filter((item) => item.filled).length}/{blocking.length}
                  </span>
                </h3>
                <ul className="wx-mw-confirm-list">
                  {blocking.map((item) => (
                    <li key={item.key} className={item.filled ? "wx-mw-confirm-item--ok" : "wx-mw-confirm-item--missing"}>
                      {item.filled ? <Check size={14} aria-hidden="true" /> : <AlertTriangle size={14} aria-hidden="true" />}
                      <span>{item.label}</span>
                      {item.detail && <em>{item.detail}</em>}
                    </li>
                  ))}
                </ul>
                {blocking.length === 0 && <p className="wx-mw-confirm-note">ระบบยังไม่ได้ส่งรายการตรวจสำหรับแรงงานรายนี้</p>}
              </section>

              {quality.length > 0 && (
                <section className="wx-mw-confirm-section">
                  <button
                    type="button"
                    className="wx-mw-confirm-toggle"
                    aria-expanded={showQuality}
                    onClick={() => setShowQuality((open) => !open)}
                  >
                    ข้อมูลเสริม (ไม่บังคับ)
                    <span className="wx-mw-confirm-count">
                      {quality.filter((item) => item.filled).length}/{quality.length}
                    </span>
                  </button>
                  {showQuality && (
                    <ul className="wx-mw-confirm-list">
                      {quality.map((item) => (
                        <li key={item.key} className={item.filled ? "wx-mw-confirm-item--ok" : "wx-mw-confirm-item--optional"}>
                          {item.filled ? <Check size={14} aria-hidden="true" /> : <span className="wx-mw-confirm-dash" aria-hidden="true" />}
                          <span>{item.label}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="wx-mw-confirm-note">
                    ข้อมูลเสริมไม่ได้ปิดกั้นการจับคู่ แต่ยิ่งครบ ระบบยิ่งเสนอตำแหน่งงานได้ตรงกับแรงงานรายนี้มากขึ้น
                  </p>
                </section>
              )}
            </>
          )}
        </div>

        {state.status === "ready" && detail && (
          <footer className="wx-mw-confirm-foot">
            {done ? (
              <div className="wx-mw-confirm-done" role="status">
                <Check size={16} aria-hidden="true" />
                <div>
                  <strong>ส่งเข้าระบบจับคู่งานแล้ว</strong>
                  <p>ชื่อนี้จะออกจากรายการรอยืนยัน และไปรออยู่ในขั้นตอน Recommendations</p>
                </div>
              </div>
            ) : (
              <>
                <div className="wx-mw-confirm-verdict">
                  {canSend ? (
                    <p className="wx-mw-confirm-ok">ข้อมูลครบตามเกณฑ์ ส่งเข้าระบบจับคู่งานได้</p>
                  ) : (
                    <div className="wx-mw-confirm-blocked">
                      <p>ยังส่งเข้าจับคู่ไม่ได้</p>
                      <ul>
                        {missingBlocking.length > 0 ? (
                          missingBlocking.map((item) => <li key={item.key}>ยังขาด: {item.label}</li>
                          )
                        ) : (
                          // Eligible on the data but the action is still not
                          // offered: that is a permission answer, and saying
                          // "ข้อมูลไม่ครบ" here would be a wrong reason.
                          <li>บัญชีของคุณไม่มีสิทธิ์ยืนยันแรงงานรายนี้ (เจ้าของสายงานหรือผู้ดูแลระบบเท่านั้น)</li>
                        )}
                      </ul>
                    </div>
                  )}
                  {submit.status === "error" && (
                    <p className="wx-mw-confirm-error" role="alert">
                      ส่งเข้าระบบจับคู่ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง
                    </p>
                  )}
                </div>
                <div className="wx-mw-confirm-actions">
                  <button type="button" className="wx-button wx-button--ghost" onClick={onClose} disabled={submitting}>
                    ยกเลิก
                  </button>
                  <button type="button" className="wx-button wx-button--primary" onClick={handleConfirm} disabled={!canSend || submitting}>
                    {submitting ? "กำลังส่ง..." : "ยืนยันและส่งเข้าจับคู่"}
                    {!submitting && <ArrowRight size={15} aria-hidden="true" />}
                  </button>
                </div>
              </>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}
